import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageLayout } from '../../shared/layouts';
import {
  DataFactoryService,
  ITenantDataFactory,
} from '../../shared/services/api/data-factory/DataFactoryService';
import { ApiException } from '../../shared/services/api/ApiException';
import {
  Box,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { feedback } from '../../shared/services';
import { FerramentasDeDetalhe, Section } from '../../shared/components';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export const DetalhesDataFactory: React.FC = () => {
  const { id = 'nova' } = useParams<'id'>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tenant, setTenant] = useState<ITenantDataFactory | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!id || id === 'nova') {
      setTenant(null);
      setIsLoading(false);
      return;
    }
    let canceled = false;
    setIsLoading(true);
    DataFactoryService.getById(id).then((result) => {
      if (canceled) return;
      setIsLoading(false);
      if (result instanceof ApiException) {
        feedback(result.message, 'error');
        setTenant(null);
        return;
      }
      setTenant(result.data);
    });
    return () => {
      canceled = true;
    };
  }, [id]);

  const title = useMemo(() => tenant?.idTenant ?? id, [tenant?.idTenant, id]);

  const formatDateTime = (iso?: string) => {
    if (!iso) return '-';

    try {
      return dayjs.utc(iso).local().format('DD/MM/YYYY - HH:mm:ss');
    } catch {
      return '-';
    }
  };

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'dataHora',
        headerName: 'Data',
        flex: 1,
        minWidth: 200,
        type: 'dateTime',
        valueGetter: (params) => {
          const v = params.value as string | Date | undefined;
          if (!v) return null;
          if (v instanceof Date) return v;
          try {
            return dayjs.utc(v).local().toDate();
          } catch {
            return null;
          }
        },
        valueFormatter: (params) => {
          const v = params.value as Date | string | null | undefined;
          if (!v) return '-';
          try {
            const d = v instanceof Date ? dayjs(v).local() : dayjs.utc(String(v)).local();
            return d.format('DD/MM/YYYY - HH:mm:ss');
          } catch {
            return '-';
          }
        },
      },
      {
        field: 'ativo',
        headerName: 'Ativo',
        flex: 1,
        minWidth: 100,
        renderCell: (params) =>
          params.value ? (
            <Chip
              sx={{ display: 'flex', color: '#fff', alignItems: 'center' }}
              label="Sim"
              color="success"
              size="medium"
            />
          ) : (
            <Chip
              sx={{ display: 'flex', color: '#fff', alignItems: 'center' }}
              label="Não"
              color="error"
              size="medium"
            />
          ),
      },
      {
        field: 'usuario',
        headerName: 'Usuário',
        flex: 1,
        minWidth: 150,
      },
    ];
  }, []);

  return (
    <PageLayout
      title={title}
      tools={
        <FerramentasDeDetalhe
          isBotaoDeVoltarLoading={isLoading}
          aoClicarEmVoltar={() => navigate(-1)}
          mostrarBotaoDeVoltar
        />
      }
    >
      <Box display="flex" flexDirection="column" gap={2}>
        <Section title="Dados Gerais" margin>
          <Box>
            <Grid container spacing={2}>
              {!isLoading && (
                <Grid item xs={6} md={6} lg={6}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    gap={1}
                    alignItems="center"
                  >
                    <Typography variant="body1" fontWeight={500}>
                      IdTenant:
                    </Typography>
                    <Typography>{tenant?.idTenant}</Typography>
                  </Box>
                </Grid>
              )}
              {isLoading && (
                <Grid item xs={6} md={6} lg={6}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
              )}
              {!isLoading && (
                <Grid item xs={6} md={6} lg={6}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    gap={1}
                    alignItems="center"
                  >
                    <Typography variant="body1" fontWeight={500}>
                      Monitoramento ativo:
                    </Typography>
                    <Typography>{tenant?.ativo ? 'Sim' : 'Não'}</Typography>
                  </Box>
                </Grid>
              )}
              {isLoading && (
                <Grid item xs={6} md={6} lg={6}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
              )}
              {!isLoading && (
                <Grid item xs={6} md={6} lg={6}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    gap={1}
                    alignItems="center"
                  >
                    <Typography variant="body1" fontWeight={500}>
                      Usuário criado:
                    </Typography>
                    <Typography>
                      {tenant?.usuarioCriado ? 'Sim' : 'Não'}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {isLoading && (
                <Grid item xs={6} md={6} lg={6}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
              )}
              {!isLoading && (
                <Grid item xs={6} md={6} lg={6}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    gap={1}
                    alignItems="center"
                  >
                    <Typography variant="body1" fontWeight={500}>
                      Schema:
                    </Typography>
                    <Typography>{tenant?.schema}</Typography>
                  </Box>
                </Grid>
              )}
              {isLoading && (
                <Grid item xs={6} md={6} lg={6}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
              )}
              {!isLoading && (
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    gap={1}
                    alignItems="center"
                  >
                    <Typography variant="body1" fontWeight={500}>
                      Última migration aplicada:
                    </Typography>
                    <Typography>{formatDateTime(tenant?.migration)}</Typography>
                  </Box>
                </Grid>
              )}
              {isLoading && (
                <Grid item xs={12}>
                  <Skeleton variant="rectangular" height={40} />
                </Grid>
              )}
            </Grid>
          </Box>
        </Section>
        <Section title="Histórico" margin>
          <Box>
            <DataGrid
              autoHeight
              rows={
                tenant?.historicosTenant.map((h, index) => ({
                  id: index,
                  dataHora: h.data,
                  usuario: h.usuario,
                  ativo: h.ativo,
                })) || []
              }
              columns={columns}
              loading={isLoading}
              disableSelectionOnClick
              getRowId={(row) => row.id}
            />
          </Box>
        </Section>
      </Box>
    </PageLayout>
  );
};
